import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { motion } from "framer-motion";
import { Package, Truck, CheckCircle, ArrowRight, Tag } from "@phosphor-icons/react";

const STATUS_STEPS = ["pending", "paid", "assigned", "label_generated", "shipped", "delivered"];

export default function BuyerDashboard() {
  const { user, authHeaders, loading } = useAuth();
  const { format, code } = useCurrency();
  const [orders, setOrders] = useState([]);

  const load = async () => {
    const r = await axios.get(`${API}/orders/buyer`, { headers: authHeaders(), withCredentials: true });
    setOrders(r.data);
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (loading) return <div className="min-h-screen pt-32 text-center text-muted">Loading...</div>;
  if (!user || user.role !== "buyer") { window.location.href = "/login"; return null; }

  return (
    <div className="min-h-screen pt-24 bg-ink-950" data-testid="buyer-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="section-label">Buyer dashboard</p>
            <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">Hi {user.name?.split(" ")[0]}.</h1>
            <p className="text-muted mt-1">{user.company} · {user.country}</p>
          </div>
          <Link to="/shop" className="btn-primary">Continue sourcing <ArrowRight size={14} /></Link>
        </div>

        <div className="mt-10 grid md:grid-cols-3 gap-5">
          <Stat Icon={Package} label="Total orders" val={orders.length} />
          <Stat Icon={Truck} label="In transit" val={orders.filter((o) => ["shipped", "label_generated", "assigned"].includes(o.status)).length} />
          <Stat Icon={CheckCircle} label="Delivered" val={orders.filter((o) => o.status === "delivered").length} />
        </div>

        <h2 className="font-display text-3xl tracking-tight mt-14">Orders</h2>
        <div className="mt-6 space-y-4">
          {orders.length === 0 ? (
            <div className="glass rounded-2xl p-16 text-center" data-testid="empty-orders">
              <Package size={40} weight="duotone" className="text-saffron mx-auto" />
              <h3 className="font-display text-3xl text-cream mt-5">No orders yet</h3>
              <p className="text-muted mt-2 max-w-sm mx-auto">This is your orders page. To browse and buy spices, head over to the marketplace.</p>
              <Link to="/shop" className="btn-primary inline-flex mt-7" data-testid="browse-marketplace-btn">
                Browse the marketplace <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          ) : orders.map((o) => (
            <motion.div key={o.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-6" data-testid={`order-${o.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gold">Order #{o.id.slice(0, 8)}</div>
                  <h3 className="font-display text-xl text-cream mt-1">{o.items.length} item(s) · {format(o.total_usd)}</h3>
                  <div className="mt-2 text-sm text-muted space-y-1">
                    {o.items.map((it, i) => <div key={i}>• {it.branded_name} — {it.qty_kg} kg</div>)}
                  </div>
                </div>
                <div className="text-right">
                  <span className="chip">{o.status}</span>
                  {o.tracking_number && <div className="text-xs text-cream/80 mt-2">Track: {o.tracking_number}</div>}
                  <a href={`${API}/orders/${o.id}/label`} target="_blank" rel="noreferrer" className="block text-xs text-gold hover:text-saffron mt-2">View invoice ↗</a>
                </div>
              </div>
              {/* Progress bar */}
              <div className="mt-5">
                <div className="flex justify-between text-[10px] uppercase tracking-[0.2em] text-muted">
                  {STATUS_STEPS.map((s) => <span key={s}>{s.replace("_", " ")}</span>)}
                </div>
                <div className="mt-2 h-1 bg-ink-800 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-saffron to-gold" style={{ width: `${((STATUS_STEPS.indexOf(o.status) + 1) / STATUS_STEPS.length) * 100}%` }} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ Icon, label, val }) {
  return (
    <div className="glass rounded-2xl p-6">
      <Icon size={22} weight="duotone" className="text-saffron" />
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted mt-3">{label}</div>
      <div className="font-display text-3xl mt-1">{val}</div>
    </div>
  );
}
